"use client";

import * as React from "react";
import { Button } from "./button";
import { Progress } from "./progress";
import { Upload } from "lucide-react";

interface FileUploadProps {
  onFileSelected: (file: File) => Promise<void>;
  accept?: string;
  maxSize?: number;
}

export function FileUpload({
  onFileSelected,
  accept = "image/*",
  maxSize = 10 * 1024 * 1024, // 10MB
}: FileUploadProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState<string>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > maxSize) {
      setError(`File size must be less than ${maxSize / 1024 / 1024}MB`);
      return;
    }

    setError(undefined);
    try {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      await onFileSelected(file);
      clearInterval(interval);
      setProgress(100);
    } catch (err) {
      setError("Failed to upload file");
      setProgress(0);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className="w-full">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
          isDragging ? "border-primary bg-primary/5" : "border-muted"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFile(file);
            }
          }}
        />
        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <div className="text-sm text-muted-foreground mb-2">
          Drag and drop your photo here, or{" "}
          <Button
            variant="link"
            className="px-1"
            // onClick={() => fileInputRef.current?.click()}
          >
            browse
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">
          Supports: JPG, PNG (max {maxSize / 1024 / 1024}MB)
        </div>
      </div>

      {progress > 0 && (
        <div className="mt-4">
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {error && <div className="mt-2 text-sm text-destructive">{error}</div>}
    </div>
  );
}
